/**
 * Shared extraction-polling lifecycle hook for the contract surfaces.
 *
 * Owns the three coordinated effects that keep an Apollo
 * `getContractExtraction` query in sync with the backend's PROCESSING
 * → terminal-status transition:
 *
 *   1. Start polling (every {@link EXTRACTION_POLL_INTERVAL_MS}) while
 *      `isExtracting` is true; stop on transition to false and on
 *      unmount.
 *   2. Detect the `PROCESSING → (PENDING_REVIEW | COMPLETED)` edge and
 *      fire the optional `onSuccessToast` exactly once per transition.
 *      The internal previous-status ref is reset whenever
 *      `contextKey` changes so a switch from Player A's PROCESSING
 *      drawer to Player B's already-PENDING_REVIEW drawer never fires
 *      a bogus success toast attributed to Player B.
 *   3. Safety timeout — after {@link EXTRACTION_MAX_POLL_DURATION_MS}
 *      stops polling and surfaces a timeout error scoped to the
 *      `contextKey` that was active when the timer started, so a
 *      timeout on one (player, documentType) context never leaks into
 *      a subsequently opened context.
 *
 * Extracted from `ContractDrawerContainer` so the mobile full-page
 * route (`ContractScreenContainer`) can mount the same lifecycle
 * without duplicating the three effects (SE-4829).
 * @module features/contracts/hooks/useContractExtractionPolling
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  EXTRACTION_MAX_POLL_DURATION_MS,
  EXTRACTION_POLL_INTERVAL_MS,
} from "../constants";
import type { ContractExtraction } from "../types";

/** Inputs accepted by {@link useContractExtractionPolling}. */
export interface UseContractExtractionPollingInput {
  /** Loaded extraction (may be null while no extraction exists yet). */
  readonly extraction: ContractExtraction | null;
  /** Apollo `startPolling` from the underlying `useContractExtraction`. */
  readonly startPolling: (intervalMs: number) => void;
  /** Apollo `stopPolling` from the underlying `useContractExtraction`. */
  readonly stopPolling: () => void;
  /**
   * Whether a manual `triggerContractExtraction` mutation is in flight.
   * Folded into `isExtracting` so the UI gates remain disabled during
   * the brief window between the trigger response and the first PROCESSING
   * poll result.
   */
  readonly isTriggering: boolean;
  /**
   * Caller-supplied opaque context key. Two responsibilities:
   *   - Resets the success-toast tracker whenever it changes.
   *   - Scopes the safety-timeout flag so it cannot leak across
   *     contexts (e.g. switching players or document types).
   * Mobile passes `${playerId}:${documentType}`, desktop passes the
   * drawer's `${open}:${playerId ?? ""}:${documentType}` key.
   */
  readonly contextKey: string;
  /**
   * Optional callback fired on the PROCESSING → (PENDING_REVIEW |
   * COMPLETED) edge. Desktop wires a success toast; mobile may opt out.
   */
  readonly onSuccessToast?: () => void;
}

/** Outputs returned by {@link useContractExtractionPolling}. */
export interface UseContractExtractionPollingResult {
  /** Whether the surface should treat extraction as active right now. */
  readonly isExtracting: boolean;
  /** Whether the safety-net timeout fired for the current contextKey. */
  readonly pollTimedOut: boolean;
  /**
   * User-facing error string for the current state, or null when the
   * surface has no error to render. Combines safety-timeout and
   * server-side FAILED status — callers that own additional error
   * sources (e.g. trigger-mutation rejections) compose them with
   * coalescing operators on the consumer side.
   */
  readonly extractionError: string | null;
  /**
   * Clears the safety-timeout flag for the current contextKey so a
   * retry path (e.g. user re-presses Extract after a timeout) can
   * unblock isExtracting before a new trigger mutation lands.
   */
  readonly resetPollTimedOut: () => void;
}

/**
 * Wires the three extraction-polling effects into a single hook so
 * desktop drawer and mobile full-page surfaces can mount identical
 * lifecycle behaviour.
 * @param input - Polling inputs (see {@link UseContractExtractionPollingInput})
 * @returns Derived polling flags + error string
 */
export function useContractExtractionPolling(
  input: UseContractExtractionPollingInput
): UseContractExtractionPollingResult {
  const {
    extraction,
    startPolling,
    stopPolling,
    isTriggering,
    contextKey,
    onSuccessToast,
  } = input;

  // Stable ref so the success-toast effect's deps stay narrow — the
  // caller can pass a fresh inline closure each render without
  // re-firing the toast detection.
  const onSuccessToastRef = useRef<(() => void) | undefined>(onSuccessToast);

  // Tracks the contextKey whose safety-timeout actually fired. Storing
  // the key (rather than a plain boolean) auto-scopes the flag so a
  // timeout on contextKey "A" stays attributed to "A" — a render
  // against contextKey "B" will see pollTimedOut=false without any
  // explicit reset.
  const [timedOutContextKey, setTimedOutContextKey] = useState<string | null>(
    null
  );
  const pollTimedOut = timedOutContextKey === contextKey;

  const isExtracting =
    !pollTimedOut &&
    (isTriggering ||
      (extraction !== null && extraction.status === "PROCESSING"));

  const previousStatusRef = useRef<string | null>(null);

  const extractionError = useMemo<string | null>(() => {
    if (pollTimedOut) {
      return "Extraction is taking longer than expected. Please try again.";
    }
    if (extraction?.status === "FAILED") {
      return "Extraction failed. Please try again.";
    }
    return null;
  }, [pollTimedOut, extraction?.status]);

  const resetPollTimedOut = useCallback(() => {
    setTimedOutContextKey(null);
  }, []);

  const result = useMemo(
    () => ({ isExtracting, pollTimedOut, extractionError, resetPollTimedOut }),
    [isExtracting, pollTimedOut, extractionError, resetPollTimedOut]
  );

  useEffect(() => {
    onSuccessToastRef.current = onSuccessToast;
  }, [onSuccessToast]);

  useEffect(() => {
    if (isExtracting) {
      startPolling(EXTRACTION_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isExtracting, startPolling, stopPolling]);

  // Seed before every contextKey switch with the new context's current
  // status so cross-context PROCESSING → terminal comparisons can't fire
  // a bogus success toast, while still letting in-context transitions
  // (e.g. A:PROCESSING → B:PROCESSING → B:COMPLETED) reach the toast
  // detector. Clearing to `null` instead would drop the PROCESSING
  // baseline mid-switch and silently swallow B's completion.
  useEffect(() => {
    previousStatusRef.current = extraction?.status ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional contextKey-only seed; folding `extraction?.status` into deps would overwrite the ref every status tick and break the PROCESSING → terminal edge detection in the success-toast effect below.
  }, [contextKey]);

  // Clear the safety-timeout latch once the extraction reaches a
  // terminal status (COMPLETED / FAILED) for the same contextKey.
  // Without this, a refetch or cache update that brings the extraction
  // back to a known terminal state after a timeout fired would leave
  // the timeout banner winning over the real status indefinitely.
  useEffect(() => {
    if (
      timedOutContextKey === contextKey &&
      extraction !== null &&
      extraction.status !== "PROCESSING"
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing the safety-timeout latch to the live extraction status is the effect's purpose; the alternative (computing this in render) would re-fire the cleanup branch every render.
      setTimedOutContextKey(null);
    }
  }, [timedOutContextKey, contextKey, extraction]);

  useEffect(() => {
    const currentStatus = extraction?.status ?? null;
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = currentStatus;
    if (
      previousStatus === "PROCESSING" &&
      (currentStatus === "PENDING_REVIEW" || currentStatus === "COMPLETED")
    ) {
      onSuccessToastRef.current?.();
    }
  }, [extraction?.status]);

  useEffect(() => {
    if (!isExtracting) return;
    // Capture the contextKey at effect-run time so the eventual
    // setTimedOutContextKey can't be misattributed if the hook
    // re-renders against a different context before the timer fires.
    const key = contextKey;
    const timeout = setTimeout(() => {
      stopPolling();
      setTimedOutContextKey(key);
    }, EXTRACTION_MAX_POLL_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [isExtracting, stopPolling, contextKey]);

  return result;
}
